import { cloneDeep, isNil, last } from 'lodash';

import { createEmptyRowEntries, createEmptyTableEntries, stringifyCellContent } from './tableInitialisationUtils';
import {
    CellData,
    CellSelection,
    CellSelections,
    TableContentData,
    TableElementChanges,
} from '../../../../common/table/TableTypes';
import { getMaxCellSelection } from './tableCellSelectionUtils';
import { continueCellFormatting, getDateTimeValuesFromInputString } from './tableCellFormattingUtils';
import convertHtmlToDraft from '../../../../common/utils/editor/convertHtmlToDraft';
import Core from 'handsontable/core';
import { CellTypeNames } from '../cellTypes/CellTypeConstants';

/**************************
 * TABLE DATA INFORMATION
 **************************/

export const getRowCount = <T>(data: Array<Array<T>>): number => (data || []).length;
export const getColumnCount = <T>(data: Array<Array<T>>): number => (data?.[0] || []).length;

export const getFinalRowIndex = (data: Array<Array<CellData>>): number => getRowCount(data) - 1;
export const getFinalColumnIndex = (data: Array<Array<CellData>>): number => getColumnCount(data) - 1;

/**
 * Get the highest row and column indices that contain content
 */
export const lastColWithContent = (hotInstance: Core, dataArray: Array<Array<CellData>>): number | undefined => {
    if (!hotInstance) return;

    for (let colIndex = getFinalColumnIndex(dataArray); colIndex >= 0; colIndex--) {
        // Readonly cells may have a null value, but visually contain content
        const cellsMeta = hotInstance.getCellsMeta();
        const currentColCells = cellsMeta.filter((cellMeta) => cellMeta?.col === colIndex);
        const colContainsReadonlyCells = currentColCells.some((cellMeta) => cellMeta.readOnly);

        if (!hotInstance.isEmptyCol(colIndex) || colContainsReadonlyCells) {
            return colIndex;
        }
    }
};

export const lastRowWithContent = (hotInstance: Core, dataArray: Array<Array<CellData>>): number | undefined => {
    if (!hotInstance) return;

    for (let rowIndex = getFinalRowIndex(dataArray); rowIndex >= 0; rowIndex--) {
        // Readonly cells may have a null value, but visually contain content
        const rowMeta = hotInstance.getCellMetaAtRow(rowIndex);
        const rowContainsReadonlyCells = rowMeta.some(({ cellData }): boolean => !!cellData?.readOnly);

        if (!hotInstance.isEmptyRow(rowIndex) || rowContainsReadonlyCells) {
            return rowIndex;
        }
    }
};

/**
 * Check if the selection is within the bounds of the data array
 * This is useful in situations like deleting rows, to make sure the selection is still valid
 */
export const selectionExistsInData = (dataArray: Array<Array<CellData>>, cellSelections: CellSelections): boolean => {
    let maxRow: number | null = null;
    let maxCol: number | null = null;

    cellSelections?.forEach((cellSelection: CellSelection) => {
        const { row, col } = getMaxCellSelection(cellSelection);
        if (isNil(maxRow) || row > maxRow) maxRow = row;
        if (isNil(maxCol) || col > maxCol) maxCol = col;
    });

    return !isNil(maxRow) && !isNil(maxCol) && maxRow < getRowCount(dataArray) && maxCol < getColumnCount(dataArray);
};

/**************************
 * TABLE DATA REORDERING
 **************************/

/**
 * Move sub-content of an array to a final index. This is an in place operation.
 */
export const shiftRowsOrColumns = <T>(movedDataIndices: Array<number>, array: Array<T>, finalIndex: number): void => {
    // remove moving part from data array
    const moving = array.splice(movedDataIndices[0], movedDataIndices.length);

    // put moving part into new spot
    array.splice(finalIndex, 0, ...moving);
};

/**
 * This function is adjust table size to be nRows x nCols
 */
export const adjustTableContentDataSize = (
    dataArray: Array<Array<CellData>>,
    nRows: number,
    nCols: number,
): Array<Array<CellData>> => {
    const nRowsPrev = getRowCount(dataArray);
    const nColsPrev = getColumnCount(dataArray);

    const newDataArray = cloneDeep(dataArray);

    if (newDataArray.length < nRows) {
        newDataArray.push(...createEmptyTableEntries(nRows - newDataArray.length, nCols));
    }

    if (newDataArray.length > nRows) {
        newDataArray.splice(nRows, newDataArray.length - nRows);
    }

    newDataArray.forEach((row) => {
        if (row.length < nCols) {
            row.push(...createEmptyRowEntries(nCols - row.length));
        }

        if (row.length > nCols) {
            row.splice(nCols, row.length - nCols);
        }
    });

    return continueCellFormatting({
        dataArray: newDataArray,
        colIndex: nColsPrev,
        nColsAdded: nCols - nColsPrev,
        rowIndex: nRowsPrev,
        nRowsAdded: nRows - nRowsPrev,
    });
};

/**************************
 * TABLE DATA PROVISION
 **************************/

export const parseTableHTML = (html: string): { values: string[][]; colWidthsPx: number[] } | null => {
    if (!/(<table)|(<TABLE)/g.test(html)) return null;

    const containerElement = document.createElement('div');
    containerElement.innerHTML = html;
    const tableElement = containerElement.querySelector('table');

    if (!tableElement) return null;

    const colWidthsPx: number[] = [];
    tableElement.querySelectorAll('colgroup > col').forEach((colElement: unknown) => {
        if (colElement instanceof HTMLTableColElement) colWidthsPx.push(parseInt(colElement.width));
    });

    const rowElements = tableElement.querySelectorAll('tr');

    const values: string[][] = [];
    rowElements.forEach((rowElement) => {
        values.push([]);
        rowElement.querySelectorAll('td, th').forEach((entryElement) => {
            last(values)?.push(stringifyCellContent(convertHtmlToDraft(entryElement.innerHTML)));
        });
    });

    return { values, colWidthsPx };
};

/**************************
 * TABLE DATA CONVERSIONS
 **************************/

/**
 * Convert any values that we want to save differently in redux to hotTable
 * @param cellData
 */
export const convertReduxValueToHotValue = (cellData: CellData): CellData => {
    const { type, value } = cellData;
    if (!value || !type) return cellData;

    // If the cell is a date time, convert the value to a numeric dateTime string
    if (type?.name === CellTypeNames.DATE_TIME) {
        const dateValues = getDateTimeValuesFromInputString(cellData.value, type);
        if (dateValues) cellData.value = dateValues.numericDateTimeString;
    }

    return cellData;
};

/**
 * Convert any values that we want to save differently in redux from hotTable
 * @param data
 */
export const convertDataToReduxFormat = (data: TableContentData): TableContentData => {
    return data?.map((row: Array<CellData>) =>
        row?.map((cellData: CellData) => {
            const { type, value } = cellData;
            if (!value || !type) return cellData;

            // If the cell is a date time, convert the value to an epoch time
            if (type?.name === CellTypeNames.DATE_TIME) {
                const dateValues = getDateTimeValuesFromInputString(cellData.value, type);
                if (dateValues) cellData.value = dateValues.epochTime.toString();
            }

            return cellData;
        }),
    );
};

/**
 * Take in an object of changes to the element object.
 * Convert any values that we want to save differently in redux
 * @param changes
 */
export const convertTableElementChangesToReduxFormat = (changes: TableElementChanges): TableElementChanges => {
    const newChanges = cloneDeep(changes);
    if (!newChanges.tableContent?.data) return changes;

    newChanges.tableContent.data = convertDataToReduxFormat(newChanges.tableContent?.data);
    return newChanges;
};

export const getValueArray = (data: TableContentData): Array<Array<string | null>> =>
    data.map((rows) => rows.map((cell) => cell.value || null)) || [];
